import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  CommandsResponse,
  CommandinfoType,
  CommandToSend,
  DeviceModels,
  AccessoryModels,
  BatchCommandRequest,
  BatchCommandListItem,
  BatchCommandFilter,
  BatchCommandListResponse,
  BatchCommandDetailResponse,
  ComboResp,
} from '../models/commands.model';
import { Buffer } from 'buffer';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CommandsService {
  private apiServerUrl = environment.loganConfig.url;

  constructor(private http: HttpClient) {}
  headers = new HttpHeaders().set('Content-Type', 'text/plain; charset=utf-8');
  requestOptions: Object = {
    headers: this.headers,
    responseType: 'text',
    withCredentials: true,
  };

  public searchCommandsResponse(
    serial: any[],
    dataEpochInit: number,
    dataEpochEnd: number
  ): Observable<String> {
    return this.http.post<String>(
      `${this.apiServerUrl}/api/comandos/searchCommand/${dataEpochInit}/${dataEpochEnd}`,
      JSON.stringify(serial),
      { withCredentials: true }
    );
  }

  public postSendCommand(
    values: CommandToSend,
    userId?: number,
    forceCommand?: boolean,
    isASCII?: boolean
  ): Observable<any> {
    var valueAux = new CommandToSend();
    valueAux.sistema = values.sistema;
    valueAux.serial = values.serial;
    valueAux.tipo = values.tipo;
    valueAux.timeout = values.timeout ? values.timeout : undefined;
    valueAux.param = values.param;
    if(values.idAccessory != -1 && values.idAccessory != null && values.idAccessory != 0) {
      valueAux.idAccessory = values.idAccessory;
    }
    if (forceCommand) {
      return this.http
        .post<String[]>(
          `${this.apiServerUrl}/api/comandos/newCommandForceGateway/${
            isASCII ? '1' : '0'
          }`,
          { request: valueAux, userId: userId?.toString() },
          { withCredentials: true }
        )
        .pipe(
          map((data) => {
            return data;
          }),
          catchError((error): Observable<any> => {
            return throwError(error);
          })
        );
    } else {
      return this.http
        .post<String[]>(
          `${this.apiServerUrl}/api/comandos/newCommand`,
          { request: valueAux, userId: userId?.toString() },
          { withCredentials: true }
        )
        .pipe(
          map((data) => {
            return data;
          }),
          catchError((error): Observable<any> => {
            return throwError(error);
          })
        );
    }
  }

  public getCancelCommands(
    solicitacaoComandoId: string,
    system: string
  ): Observable<String> {
    return this.http.get<String>(
      `${this.apiServerUrl}/api/comandos/cancel/${solicitacaoComandoId}/${system}`,
      this.requestOptions
    );
  }
  public getCommandsInfoType(): Observable<CommandinfoType[]> {
    return this.http.get<CommandinfoType[]>(
      `${this.apiServerUrl}/api/comandos/requestCommandInfoType`
    );
  }

  //CommandInfo
  public getDeviceModels(): Observable<DeviceModels[]> {
    return this.http.get<DeviceModels[]>(
      `${this.apiServerUrl}/api/v1/getDeviceModels`
    );
  }

    //CommandInfo
  public getAccessoryModels(): Observable<AccessoryModels[]> {
    return this.http.get<AccessoryModels[]>(
      `${this.apiServerUrl}/api/v1/getAccessoryModels`
    );
  }

  public insertInfoCommand(commandInfo: CommandinfoType) {
    return this.http.post<CommandinfoType[]>(
      `${this.apiServerUrl}/api/comandos/insertCommandInfoType`,
      commandInfo,
      { withCredentials: true }
    );
  }
  public updateInfoCommand(commandInfo: CommandinfoType) {
    return this.http.post<CommandinfoType[]>(
      `${this.apiServerUrl}/api/comandos/updateCommandInfoType`,
      commandInfo,
      { withCredentials: true }
    );
  }
  public excludeInfoCommand(commandInfo: CommandinfoType) {
    return this.http.post<CommandinfoType[]>(
      `${this.apiServerUrl}/api/comandos/deleteCommandInfoType`,
      commandInfo,
      { withCredentials: true }
    );
  }

  public postBatchCommand(payload: BatchCommandRequest): Observable<any> {
    return this.http.post<any>(
      `${this.apiServerUrl}/api/comandos/batchCommands`,
      payload,
      { withCredentials: true }
    );
  }

  public getBatchCommandsList(
    filter: BatchCommandFilter,
    page: number = 1,
    size: number = 50
  ): Observable<BatchCommandListResponse> {
    let params: any = { page, size };
    if (filter.nome) {
      params.nome = filter.nome;
    }
    if (filter.dataInicial) {
      params.dataInicial = filter.dataInicial;
    }
    if (filter.dataFinal) {
      params.dataFinal = filter.dataFinal;
    }
    if (filter.idComando) {
      params.idComando = filter.idComando;
    }
    if (filter.status) {
      params.status = filter.status;
    }
    if (filter.serial) {
      params.serial = filter.serial;
    }
    return this.http.get<BatchCommandListResponse>(
      `${this.apiServerUrl}/api/comandos/batchCommands`,
      { params, withCredentials: true }
    );
  }

  public cancelBatchCommand(id: number): Observable<any> {
    // backend responde com o texto "OK" (não é JSON válido), por isso responseType 'text'
    return this.http.post(
      `${this.apiServerUrl}/api/comandos/batchCommands/${id}/cancel`,
      null,
      { withCredentials: true, responseType: 'text' }
    );
  }

  public exportBatchCommandsCsv(id: number): Observable<Blob> {
    return this.http.get(
      `${this.apiServerUrl}/api/comandos/batchCommands/export/csv`,
      { params: { id }, withCredentials: true, responseType: 'blob' }
    );
  }

  public getBatchCommandDetail(
    id: number,
    page: number = 1,
    size: number = 100
  ): Observable<BatchCommandDetailResponse> {
    return this.http.get<BatchCommandDetailResponse>(
      `${this.apiServerUrl}/api/comandos/batchCommand/detail`,
      { params: { id, page, size }, withCredentials: true }
    );
  }

  public getBatchCommandDevices(): Observable<ComboResp[]> {
    return this.http.get<ComboResp[]>(
      `${this.apiServerUrl}/api/comandos/batchCommand/device`,
      { withCredentials: true }
    );
  }

  public getBatchCommandFirmwares(device: string): Observable<ComboResp[]> {
    return this.http.get<ComboResp[]>(
      `${this.apiServerUrl}/api/comandos/batchCommand/device/firmware`,
      { params: { device }, withCredentials: true }
    );
  }
}
